import React from 'react'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {MBContentSubTitleTypography} from '../../Atoms/Typographies'
import {MBRequiredBadge} from '../../Atoms/Badges'
import {Spacer} from '../../../../Common/Components/Spacer'
import {MBMemberInput} from '../../Atoms/MBMemberInput'
import {PIMWarningMessages} from '../../Atoms/Labels/PIMWarningMessages'
import {convertOnBlur} from '../../../Utils/ConvertOnBlur'

export type MemberNameErrors = {
  familyNameErrors: string[]
  firstNameErrors: string[]
  familyNameKanaErrors: string[]
  firstNameKanaErrors: string[]
}

export type MemberName = {
  familyName: string,
  firstName: string,
  familyNameKana: string,
  firstNameKana: string,
}

type MemberNameInputsProps = {
  memberName: MemberName
  memberNameErrors: MemberNameErrors
  onMemberNameChange: (memberName: MemberName) => void
}
export const MemberNameInputs: React.VFC<MemberNameInputsProps> = (
  {
    memberName,
    memberNameErrors,
    onMemberNameChange,
  },
) => {
  const {
    familyName, firstName,
    familyNameKana, firstNameKana,
  } = memberName
  const {
    familyNameErrors, firstNameErrors,
    familyNameKanaErrors, firstNameKanaErrors,
  } = memberNameErrors

  const updateMemberName = (
    name: string,
    value: string,
    memberName: MemberName,
  ): MemberName => ({
    ...memberName,
    [name]: value,
  })

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    memberName: MemberName,
  ): MemberName => {
    const {
      name,
      value,
    } = e.target
    return updateMemberName(name, value, memberName)
  }

  const handleBlurInput = (
    e: React.FocusEvent<HTMLInputElement>,
    memberName: MemberName,
  ): MemberName => {
    const {name, value} = e.target
    return updateMemberName(name, convertOnBlur(name, value), memberName)
  }

  return (<>
    <div data-testid={'block-input-name-info'}>
      <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
        <MBContentSubTitleTypography>お名前</MBContentSubTitleTypography>
        <MBRequiredBadge/>
      </PIMStack>
      <Spacer mt={'10px'}>
        <div data-testid={'input-family-name'}>
          <PIMStack spacing={1}>
            <label htmlFor="family-name-input">姓</label>
            <MBMemberInput
              type="text"
              name="familyName"
              autoComplete="family-name"
              className="form-text_01 w_100"
              value={familyName}
              id="family-name-input"
              placeholder="例）山田"
              hasError={familyNameErrors.length > 0}
              onChange={(e): void => {
                onMemberNameChange(handleChangeInput(e, memberName))
              }}
              onBlur={(e): void => {
                onMemberNameChange(handleBlurInput(e, memberName))
              }}
            />
          </PIMStack>
          {familyNameErrors.length > 0 && (
            <Spacer mt={'10px'}>
              <PIMWarningMessages messages={familyNameErrors}/>
            </Spacer>
          )}
        </div>
      </Spacer>
      <Spacer mt={'16px'}>
        <div data-testid={'input-first-name'}>
          <PIMStack spacing={1}>
            <label htmlFor="first-name-input">名</label>
            <MBMemberInput
              type="text"
              name="firstName"
              autoComplete="given-name"
              className="form-text_01 w_100"
              value={firstName}
              id="first-name-input"
              placeholder="例）太郎"
              hasError={firstNameErrors.length > 0}
              onChange={(e): void => {
                onMemberNameChange(handleChangeInput(e, memberName))
              }}
              onBlur={(e): void => {
                onMemberNameChange(handleBlurInput(e, memberName))
              }}
            />
          </PIMStack>
          {firstNameErrors.length > 0 && (
            <Spacer mt={'10px'}>
              <PIMWarningMessages messages={firstNameErrors}/>
            </Spacer>
          )}
        </div>
      </Spacer>
    </div>

    <div data-testid={'block-input-kana-info'}>
      <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
        <MBContentSubTitleTypography>フリガナ</MBContentSubTitleTypography>
        <MBRequiredBadge/>
      </PIMStack>
      <Spacer mt={'10px'}>
        <div data-testid="input-family-name-kana">
          <PIMStack spacing={1}>
            <label htmlFor="family-name-kana-input">セイ</label>
            <MBMemberInput
              type="text"
              name="familyNameKana"
              className="form-text_01 w_100"
              value={familyNameKana}
              id="family-name-kana-input"
              placeholder="例）ヤマダ"
              hasError={familyNameKanaErrors.length > 0}
              onChange={(e): void => {
                onMemberNameChange(handleChangeInput(e, memberName))
              }}
              onBlur={(e): void => {
                onMemberNameChange(handleBlurInput(e, memberName))
              }}
            />
          </PIMStack>
          {familyNameKanaErrors.length > 0 && (
            <Spacer mt={'10px'}>
              <PIMWarningMessages messages={familyNameKanaErrors}/>
            </Spacer>
          )}
        </div>
      </Spacer>
      <Spacer mt={'16px'}>
        <div data-testid={'input-first-name-kana'}>
          <PIMStack spacing={1}>
            <label htmlFor="first-name-kana-input">メイ</label>
            <MBMemberInput
              type="text"
              name="firstNameKana"
              className="form-text_01 w_100"
              value={firstNameKana || ''}
              id="first-name-kana-input"
              placeholder="例）タロウ"
              hasError={firstNameKanaErrors.length > 0}
              onChange={(e): void => {
                onMemberNameChange(handleChangeInput(e, memberName))
              }}
              onBlur={(e): void => {
                onMemberNameChange(handleBlurInput(e, memberName))
              }}
            />
          </PIMStack>
          {firstNameKanaErrors.length > 0 && (
            <Spacer mt={'10px'}>
              <PIMWarningMessages messages={firstNameKanaErrors}/>
            </Spacer>
          )}
        </div>
      </Spacer>
    </div>
  </>)
}
